// Nơi bế quan (CULTIVATION §2.2) và hệ số Mạt Pháp cục bộ theo vùng (WORLD §3.1, §6.2).
// Chỉ khai các nơi của giai đoạn đã mở; nơi P1+ thêm khi giai đoạn đó mở.
import { BALANCE } from '../balance';
import type { Element } from '../elements';
import type { Phase } from '../screens';
import { stageL } from '../powerScale';

export type VeinGrade = keyof typeof BALANCE.VEIN_GRADE_MULT;
export type SiteKind = 'cave' | 'spiritVein' | 'resourceZone' | 'secretRealm' | 'sectSite';

export interface CultivationSite {
  readonly id: string;
  /** Vùng tài nguyên (WORLD §6.2): M và sự kiện thế giới gắn theo vùng. */
  readonly zoneId: string;
  /** Cảnh giới gốc 0 của nơi này (r): chọn B_vùng và phạt "thấp hơn ≥ 2 bậc". */
  readonly realmTier: number;
  readonly veinGrade: VeinGrade;
  /** Khe ngoài (tạp dịch): ×OUTER_SEAT_MULT. */
  readonly outerSeat?: boolean;
  readonly element: Element | null;
  /** 'cave' = động phủ của người chơi: chỉ ở đây Tụ Linh Trận và hiệu suất offline cộng thêm có tác dụng. */
  readonly kind: SiteKind;
  readonly phase: Phase;
}

export const CULTIVATION_SITES: readonly CultivationSite[] = [
  {
    id: 'site.lk.servantQuarters',
    zoneId: 'res.thanhHuVein',
    realmTier: 0,
    veinGrade: 'low',
    outerSeat: true,
    element: 'wood',
    kind: 'cave',
    phase: 'P0',
  },
  {
    id: 'site.lk.wolfValley',
    zoneId: 'res.langCoc',
    realmTier: 0,
    veinGrade: 'none',
    element: 'metal',
    kind: 'resourceZone',
    phase: 'P0',
  },
];

export const DEFAULT_SITE_ID = 'site.lk.servantQuarters';

export function siteDef(id: string): CultivationSite {
  const s = CULTIVATION_SITES.find((x) => x.id === id);
  if (!s) throw new RangeError(`unknown cultivation site: ${id}`);
  return s;
}

export function isSiteId(v: unknown): v is string {
  return typeof v === 'string' && CULTIVATION_SITES.some((s) => s.id === v);
}

/**
 * Sự kiện thế giới đổi `M` của một vùng khi người chơi chạm một bước tiểu cảnh (tất định, không
 * cần cờ: bước không bao giờ lùi). `fromStep` là bước toàn cục n = L − 1.
 */
export interface ZoneVeinEvent {
  readonly id: string;
  readonly zoneId: string;
  readonly fromStep: number;
  readonly mult: number;
  /** Khóa i18n tin Thế Sự khi sự kiện xảy ra (Hồi Sơn, Truyền Âm kênh Thế Sự). */
  readonly newsKey: string;
  readonly phase: Phase;
}

export const ZONE_VEIN_EVENTS: readonly ZoneVeinEvent[] = [
  {
    id: 'veinDry',
    zoneId: 'res.thanhHuVein',
    // "Từ LK5" (WORLD §6.2): suy từ thang, không đặt tay số bước.
    fromStep: stageL(1, 5) - 1,
    mult: BALANCE.VEIN_DRY_MULT,
    newsKey: 'news.veinDry',
    phase: 'P0',
  },
];

/** M của một vùng ở bước `step` (tích mọi sự kiện đã xảy ra). */
export function zoneVeinMult(zoneId: string, step: number): number {
  let m = 1;
  for (const e of ZONE_VEIN_EVENTS) if (e.zoneId === zoneId && step >= e.fromStep) m *= e.mult;
  return m;
}
